import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft, ArrowRight, ExternalLink } from 'lucide-react';
import { projectsData } from '../data/mock';

const categories = ['All', 'SaaS', 'Mobile', 'Web', 'UX'];

const WorkPage = () => {
  const { id } = useParams();
  const [activeCategory, setActiveCategory] = useState('All');
  const [hoveredProject, setHoveredProject] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observe all elements with reveal-on-scroll class
    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [id, activeCategory]); // Re-run observer when category changes

  // Project Detail View
  if (id) {
    const project = projectsData.find((p) => p.id === parseInt(id));
    const currentIndex = projectsData.findIndex((p) => p.id === parseInt(id));
    const prevProject = projectsData[currentIndex - 1];
    const nextProject = projectsData[currentIndex + 1];

    if (!project) {
      return (
        <div className="pt-32 pb-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Project Not Found</h1>
            <Link to="/work">
              <button className="px-8 py-4 bg-[#00ff88] text-black rounded-full font-semibold">
                Back to Projects
              </button>
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="relative">
        <div className="gradient-bg" />
        <div className="grid-bg" />
        <div className="noise-overlay" />

        {/* Hero */}
        <section className="pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Link
              to="/work"
              className="inline-flex items-center gap-2 text-white/50 hover:text-[#00ff88] mb-8 transition-colors group"
            >
              <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
              Back to Projects
            </Link>

            <div className="max-w-4xl">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="px-4 py-1.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium">
                  {project.category}
                </span>
                <span className="text-white/40">{project.year}</span>
              </div>
              <h1 
                className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6"
                style={{ animation: 'fadeUp 1s 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(40px)' }}
              >
                {project.title}
              </h1>
              <div className="flex flex-wrap gap-3">
                {project.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-4 py-2 rounded-full bg-white/5 text-white/60 text-sm border border-white/10"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Hero Image */}
        <section className="pb-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div 
              className="relative rounded-3xl overflow-hidden"
              style={{ animation: 'fadeUp 1s 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(40px)' }}
            >
              <img
                src={project.heroImage}
                alt={project.title}
                className="w-full aspect-[16/9] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>
          </div>
        </section>

        {/* Case Study */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-10">
            <div className="space-y-20">
              {[
                { label: 'The Challenge', title: 'Problem', content: project.problem },
                { label: 'The Approach', title: 'Process', content: project.process },
                { label: 'The Result', title: 'Solution', content: project.solution },
              ].map((section, index) => (
                <div key={section.title} className="reveal-on-scroll stagger-children">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium mb-4">
                    {section.label}
                  </span>
                  <h2 className="text-4xl font-bold text-white mb-6">{section.title}</h2>
                  <p className="text-white/60 text-xl leading-relaxed">{section.content}</p>
                </div>
              ))}

              {/* Outcome - Special styling */}
              <div className="reveal-on-scroll stagger-children glass-card p-10 lg:p-12">
                <span className="inline-block px-4 py-1.5 rounded-full bg-[#7c3aed]/10 text-[#7c3aed] text-sm font-medium mb-4">
                  Impact
                </span>
                <h2 className="text-4xl font-bold text-white mb-6">Outcome</h2>
                <p className="text-white text-xl leading-relaxed">{project.outcome}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation */}
        <section className="py-20 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex flex-col md:flex-row justify-between gap-8">
              {prevProject ? (
                <Link
                  to={`/work/${prevProject.id}`}
                  className="group flex-1 glass-card p-8 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-center gap-2 text-white/40 mb-3">
                    <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                    Previous Project
                  </div>
                  <p className="text-2xl font-bold text-white group-hover:text-[#00ff88] transition-colors">
                    {prevProject.title}
                  </p>
                </Link>
              ) : (
                <div className="flex-1" />
              )}

              {nextProject ? (
                <Link
                  to={`/work/${nextProject.id}`}
                  className="group flex-1 glass-card p-8 text-right hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-center justify-end gap-2 text-white/40 mb-3">
                    Next Project
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-2xl font-bold text-white group-hover:text-[#00ff88] transition-colors">
                    {nextProject.title}
                  </p>
                </Link>
              ) : (
                <div className="flex-1" />
              )}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Projects List View
  const filteredProjects =
    activeCategory === 'All'
      ? projectsData
      : projectsData.filter((p) => p.category === activeCategory);

  return (
    <div className="relative">
      <div className="gradient-bg" />
      <div className="grid-bg" />
      <div className="noise-overlay" />

      {/* Header */}
      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-4xl">
            <span 
              className="inline-block px-4 py-1.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium mb-6"
              style={{ animation: 'fadeUp 0.8s 0.1s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(20px)' }}
            >
              My Portfolio
            </span>
            <h1 
              className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6"
              style={{ animation: 'fadeUp 1s 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(40px)' }}
            >
              Selected{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff88] to-[#7c3aed]">Projects</span>
            </h1>
            <p 
              className="text-white/50 text-xl leading-relaxed"
              style={{ animation: 'fadeUp 1s 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(20px)' }}
            >
              A collection of my best work, showcasing design thinking, problem-solving, and attention to detail.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div 
            className="flex flex-wrap gap-3"
            style={{ animation: 'fadeUp 1s 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(20px)' }}
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-[#00ff88] text-black'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 border border-white/10'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid gap-8" key={activeCategory}>
            {filteredProjects.map((project, index) => (
              <Link
                key={project.id}
                to={`/work/${project.id}`}
                className="group block opacity-0 animate-[fadeUp_0.6s_ease-out_forwards]"
                style={{ animationDelay: `${index * 0.1}s` }}
                onMouseEnter={() => setHoveredProject(project.id)}
                onMouseLeave={() => setHoveredProject(null)}
              >
                <div className="glass-card p-4 lg:p-6 transition-all duration-500 hover:scale-[1.01]">
                  <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-center">
                    {/* Image */}
                    <div className={`relative aspect-[16/10] rounded-2xl overflow-hidden ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                      <img
                        src={project.thumbnail}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      
                      {/* Overlay content */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                        <div className="w-20 h-20 rounded-full bg-[#00ff88] flex items-center justify-center text-black transform scale-0 group-hover:scale-100 transition-transform duration-300">
                          <ArrowUpRight size={28} />
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="space-y-6 p-4">
                      <div className="flex items-center gap-3">
                        <span className="px-4 py-1.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium">
                          {project.category}
                        </span>
                        <span className="text-white/40">{project.year}</span>
                      </div>
                      <h3 className="text-3xl lg:text-4xl font-bold text-white group-hover:text-[#00ff88] transition-colors">
                        {project.title}
                      </h3>
                      <p className="text-white/50 text-lg leading-relaxed line-clamp-2">
                        {project.problem}
                      </p>
                      <div className="flex flex-wrap gap-2 pt-4">
                        {project.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-4 py-2 rounded-full bg-white/5 text-white/60 text-sm border border-white/10"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="reveal-on-scroll stagger-children glass-card p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#00ff88]/20 to-transparent rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <h2 className="text-4xl font-bold text-white mb-4">Have a Project in Mind?</h2>
              <p className="text-white/50 text-lg mb-8">
                Let's discuss how I can help bring your vision to life.
              </p>
              <Link to="/contact">
                <button className="group px-8 py-4 bg-[#00ff88] text-black rounded-full font-semibold hover:shadow-[0_0_40px_rgba(0,255,136,0.4)] transition-all duration-300">
                  <span className="flex items-center gap-2">
                    Start a Project
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkPage;
